/**
 * FHIR Questionnaire JSON validation utilities.
 *
 * Provides validation for Questionnaire resources before saving to Medplum.
 * Validates required fields, item structure, and common mistakes.
 */
import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes';
import { validateConditionalReferences } from './conditionalQuestions';

export interface ValidationError {
  path: string;
  message: string;
  severity: 'error' | 'warning';
}

/**
 * Validate a Questionnaire resource for correctness.
 * Returns an array of validation errors (empty = valid).
 */
export function validateQuestionnaire(questionnaire: unknown): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!questionnaire || typeof questionnaire !== 'object') {
    errors.push({ path: '', message: 'Questionnaire must be a JSON object', severity: 'error' });
    return errors;
  }

  const q = questionnaire as Record<string, unknown>;

  // resourceType
  if (q.resourceType !== 'Questionnaire') {
    errors.push({
      path: 'resourceType',
      message: `resourceType must be "Questionnaire", got "${String(q.resourceType)}"`,
      severity: 'error',
    });
  }

  // status (required)
  const validStatuses = ['draft', 'active', 'retired', 'unknown'];
  if (!q.status) {
    errors.push({ path: 'status', message: 'status is required', severity: 'error' });
  } else if (!validStatuses.includes(q.status as string)) {
    errors.push({
      path: 'status',
      message: `status must be one of: ${validStatuses.join(', ')}`,
      severity: 'error',
    });
  }

  // title (strongly recommended)
  if (!q.title || typeof q.title !== 'string' || q.title.trim() === '') {
    errors.push({ path: 'title', message: 'title is recommended and should not be empty', severity: 'warning' });
  }

  // item array validation
  if (q.item !== undefined) {
    if (!Array.isArray(q.item)) {
      errors.push({ path: 'item', message: 'item must be an array', severity: 'error' });
    } else {
      validateItems(q.item as unknown[], 'item', errors);
      errors.push(...validateConditionalReferences(q.item as QuestionnaireItem[]));
    }
  }

  return errors;
}

/**
 * Recursively validate QuestionnaireItem arrays.
 */
function validateItems(items: unknown[], basePath: string, errors: ValidationError[]): void {
  const linkIds = new Set<string>();

  items.forEach((item, index) => {
    const path = `${basePath}[${index}]`;

    if (!item || typeof item !== 'object') {
      errors.push({ path, message: 'Item must be a JSON object', severity: 'error' });
      return;
    }

    const qi = item as Record<string, unknown>;

    // linkId (required, must be unique)
    if (!qi.linkId || typeof qi.linkId !== 'string') {
      errors.push({ path: `${path}.linkId`, message: 'linkId is required and must be a string', severity: 'error' });
    } else {
      if (linkIds.has(qi.linkId)) {
        errors.push({
          path: `${path}.linkId`,
          message: `Duplicate linkId "${qi.linkId}" found`,
          severity: 'error',
        });
      }
      linkIds.add(qi.linkId);
    }

    // type (required)
    const validTypes = [
      'group', 'display', 'boolean', 'decimal', 'integer', 'date', 'dateTime',
      'time', 'string', 'text', 'url', 'choice', 'open-choice', 'attachment',
      'reference', 'quantity',
    ];
    if (!qi.type || typeof qi.type !== 'string') {
      errors.push({ path: `${path}.type`, message: 'type is required', severity: 'error' });
    } else if (!validTypes.includes(qi.type)) {
      errors.push({
        path: `${path}.type`,
        message: `type "${qi.type}" is not a valid Questionnaire item type`,
        severity: 'warning',
      });
    }

    // text (recommended)
    if (!qi.text || typeof qi.text !== 'string') {
      errors.push({ path: `${path}.text`, message: 'text (question label) is recommended', severity: 'warning' });
    }

    // choice type should have answerOption
    if ((qi.type === 'choice' || qi.type === 'open-choice') && !qi.answerOption && !qi.answerValueSet) {
      errors.push({
        path: `${path}.answerOption`,
        message: 'Choice items should have answerOption or answerValueSet',
        severity: 'warning',
      });
    }

    // Nested items
    if (qi.item !== undefined) {
      if (!Array.isArray(qi.item)) {
        errors.push({ path: `${path}.item`, message: 'Nested item must be an array', severity: 'error' });
      } else {
        validateItems(qi.item, `${path}.item`, errors);
      }
    }
  });
}

/**
 * Try to parse a JSON string as a Questionnaire.
 * Returns the parsed object or validation errors.
 */
export function parseAndValidateQuestionnaireJson(
  json: string,
): { questionnaire: Questionnaire; errors: ValidationError[] } | { questionnaire: null; errors: ValidationError[] } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (err) {
    return {
      questionnaire: null,
      errors: [{ path: '', message: `Invalid JSON: ${(err as Error).message}`, severity: 'error' }],
    };
  }

  const errors = validateQuestionnaire(parsed);
  const hasErrors = errors.some((e) => e.severity === 'error');

  return {
    questionnaire: hasErrors ? null : (parsed as Questionnaire),
    errors,
  };
}
